import axios from 'axios';
import api from './api';

interface DirectMediaUploadRequest {
  file: File;
  fileType: string;
  campaignId?: string | null;
  metadata?: Record<string, unknown>;
  onUploadProgress?: (percentCompleted: number) => void;
}

interface PresignedMediaUploadResponse {
  mediaFileId: string;
  uploadUrl: string;
  fields: Record<string, string>;
}

/**
 * Upload a media file directly from the browser to object storage using the
 * backend presigned POST flow, then confirm the upload in the application API.
 */
export async function uploadMediaFileDirect({
  file,
  fileType,
  campaignId,
  metadata,
  onUploadProgress,
}: DirectMediaUploadRequest): Promise<string> {
  const presignedResponse = await api.post<PresignedMediaUploadResponse>('/api/media/presigned_upload/', {
    filename: file.name,
    fileType,
    contentType: file.type || undefined,
    fileSize: file.size,
    campaignId: campaignId ?? undefined,
    metadata: metadata ?? {},
  });

  const { mediaFileId, uploadUrl, fields } = presignedResponse.data;
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  await axios.post(uploadUrl, formData, {
    onUploadProgress: (progressEvent) => {
      if (!progressEvent.total || !onUploadProgress) {
        return;
      }
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onUploadProgress(percentCompleted);
    },
  });

  const confirmResponse = await api.post<{ id: string }>(`/api/media/${mediaFileId}/confirm_upload/`, {});
  onUploadProgress?.(100);
  return confirmResponse.data.id;
}
